/**
 * Zipytiny Application Main Entry
 * AI Knowledge Engine & Video Study Assistant
 * Build Deployment Sync: 2026.08.27 - Clean UTF-8
 */
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, lazy, Suspense, useMemo } from 'react';
import { PRICING_CONFIG, PRO_PLAN_MONTHLY_PRICE, PRO_PLAN_ANNUAL_MONTHLY_PRICE, ENTERPRISE_PLAN_MONTHLY_PRICE } from './config/pricing.config';
import { PricingCards } from './components/PricingCards';
import PlanCheckoutModal, { PlanType, BillingCycle } from './components/PlanCheckoutModal';
import SocialLogosBar from './components/SocialLogosBar';
import BrandLogo from './components/BrandLogo';
import { motion, AnimatePresence } from 'motion/react';
import {
  Youtube,
  Sparkles,
  BookOpen,
  FileText,
  Twitter,
  Share2,
  CheckCircle,
  HelpCircle,
  Award,
  Trophy,
  Network,
  ArrowRight,
  History,
  Trash2,
  Play,
  Pause,
  Volume2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Flame,
  Gift,
  Bookmark,
  RefreshCw,
  Video,
  CreditCard,
  Globe,
  ShieldCheck,
  Download,
  Zap,
  Lock,
  Crown,
  Server,
  AlertCircle,
  Info,
  ExternalLink,
  DollarSign,
  Megaphone,
  TrendingUp,
  Rocket,
  Activity,
  BarChart,
  X,
  Moon,
  Sun,
  Bell,
  Mail,
  Folder,
  FolderPlus,
  Heart,
  Search,
  Compass,
  FolderOpen,
  Puzzle,
  Facebook,
  Linkedin,
  Music
} from 'lucide-react';
import { PRELOADED_VIDEOS } from './preloadedData';
import { ARABIC_PRELOADED_VIDEOS } from './utils/arabicPreloadedData';
import { translations } from './utils/translations';
import { YouTubeSummaryResponse, SavedSummary, SynthesizedStack } from './types';
import { auth, db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  User,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { KeyRound, ShieldAlert, Eye, EyeOff, MessageSquare, Headphones, Users, Cpu, Layers, Sliders, ThumbsUp, PlayCircle, Briefcase, GraduationCap } from 'lucide-react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initGlobalErrorLogging, logClientError } from './utils/errorLogger';
import { AppScreenSkeleton, WorkspaceSkeleton, PageLoadingIndicator } from './components/LoadingSkeletons';
import { getRouteSeoMeta } from './utils/seoMetaMap';
import { useSeoMeta } from './hooks/useSeoMeta';
import HelpTooltip from './components/HelpTooltip';

const ReferralModal = lazy(() => import('./components/ReferralModal'));
const CommunityStudyHub = lazy(() => import('./components/CommunityStudyHub'));

const triggerConfetti = async (options?: any) => {
  try {
    const confetti = (await import('canvas-confetti')).default;
    confetti(options);
  } catch (e) {
    // Non-blocking fallback
  }
};

import LandingPage from './components/LandingPage';
import FloatingSupportChatWidget from './components/FloatingSupportChatWidget';

const LearningDashboardModule = lazy(() => import('./components/LearningDashboard').then(m => ({ default: m.LearningProgressDashboard })));
const ActiveLearningDashboardModule = lazy(() => import('./components/LearningDashboard').then(m => ({ default: m.ActiveLearningDashboard })));
const CinematicExplainer = lazy(() => import('./components/CinematicExplainer').then(m => ({ default: m.CinematicExplainer })));
const LearningProgressDashboard = (props: any) => <Suspense fallback={<div className="animate-pulse h-64 rounded-3xl bg-neutral-100 dark:bg-zinc-800" />}><LearningDashboardModule {...props} /></Suspense>;
const ActiveLearningDashboard = (props: any) => <Suspense fallback={<div className="animate-pulse h-64 rounded-3xl bg-neutral-100 dark:bg-zinc-800" />}><ActiveLearningDashboardModule {...props} /></Suspense>;
const AIChatWithSummaryRaw = lazy(() => import('./components/AIChatWithSummary'));
const SummaryPremiumExporterRaw = lazy(() => import('./components/SummaryPremiumExporter'));
const LearningWorkspaceRaw = lazy(() => import('./components/LearningWorkspace'));
const FeaturePageRaw = lazy(() => import('./components/FeaturePage'));
const ChromeExtensionPageRaw = lazy(() => import('./components/ChromeExtensionPage'));
const BlogPageRaw = lazy(() => import('./components/BlogPage'));
const ToolPageRaw = lazy(() => import('./components/ToolPage'));
const FaqPageRaw = lazy(() => import('./components/FaqPage').then(m => ({ default: m.FaqPage })));
const FeaturesHubPageRaw = lazy(() => import('./components/FeaturesHubPage'));
const PricingPageRaw = lazy(() => import('./components/PricingPage'));
const SalesCallsUseCasePageRaw = lazy(() => import('./components/SalesCallsUseCasePage'));
const ClientMeetingRecapUseCasePageRaw = lazy(() => import('./components/ClientMeetingRecapUseCasePage'));
const RecordingToPowerPointUseCasePageRaw = lazy(() => import('./components/RecordingToPowerPointUseCasePage'));
const ZipytinyVsOtterAiPageRaw = lazy(() => import('./components/ZipytinyVsOtterAiPage'));
const YouTubeQuizPageRaw = lazy(() => import('./components/YouTubeQuizPage'));
const StudyLibraryPageRaw = lazy(() => import('./components/StudyLibraryPage'));

const LazyLoadingFallback = () => (
  <div className="flex flex-col items-center justify-center p-12 min-h-[300px] space-y-4">
    <div className="w-8 h-8 border-3 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
    <p className="text-xs font-semibold text-neutral-400 dark:text-zinc-500 animate-pulse font-sans">Assembling knowledge workspace...</p>
  </div>
);

const AppScreenLoadingFallback = () => <AppScreenSkeleton />;

const AIChatWithSummary = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <AIChatWithSummaryRaw {...props} />
  </Suspense>
);

const SummaryPremiumExporter = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <SummaryPremiumExporterRaw {...props} />
  </Suspense>
);

const LearningWorkspace = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <LearningWorkspaceRaw {...props} />
  </Suspense>
);

const FeaturePage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <FeaturePageRaw {...props} />
  </Suspense>
);

const ChromeExtensionPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <ChromeExtensionPageRaw {...props} />
  </Suspense>
);

const BlogPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <BlogPageRaw {...props} />
  </Suspense>
);

const ToolPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <ToolPageRaw {...props} />
  </Suspense>
);

const FaqPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <FaqPageRaw {...props} />
  </Suspense>
);

const FeaturesHubPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <FeaturesHubPageRaw {...props} />
  </Suspense>
);

const PricingPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <PricingPageRaw {...props} />
  </Suspense>
);

const SalesCallsUseCasePage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <SalesCallsUseCasePageRaw {...props} />
  </Suspense>
);

const ClientMeetingRecapUseCasePage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <ClientMeetingRecapUseCasePageRaw {...props} />
  </Suspense>
);

const RecordingToPowerPointUseCasePage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <RecordingToPowerPointUseCasePageRaw {...props} />
  </Suspense>
);

const ZipytinyVsOtterAiPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <ZipytinyVsOtterAiPageRaw {...props} />
  </Suspense>
);

const YouTubeQuizPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <YouTubeQuizPageRaw {...props} />
  </Suspense>
);

const StudyLibraryPage = (props: any) => (
  <Suspense fallback={<LazyLoadingFallback />}>
    <StudyLibraryPageRaw {...props} />
  </Suspense>
);

const LoadingTimeline = ({ onComplete, loadingStep }: { onComplete?: () => void; loadingStep?: string }) => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    { label: 'Extracting transcript & mapping content assets', weight: 15 },
    { label: 'Understanding context & deep semantic parsing', weight: 35 },
    { label: 'Generating summaries & learning nodes', weight: 55 },
    { label: 'Building flashcards for active recall', weight: 75 },
    { label: 'Generating mind maps and concept schemas', weight: 90 },
    { label: 'Preparing AI Chat and study diagnostics', weight: 98 },
    { label: 'Workspace Ready', weight: 100 }
  ];

  const microPhrases = [
    "Scanning for filler words and unnecessary pauses...",
    "Extracting key concepts and linguistic milestones...",
    "Distilling golden educational nuggets...",
    "Formulating concise notes in real-time...",
    "Generating deep conceptual relationships...",
    "Drafting dynamic flashcard recall pairs...",
    "Organizing active study flashcard decks...",
    "Clustering arguments for the interactive mind map...",
    "Formatting output structures for PDF, Notion & Word...",
    "Calibrating conversational AI tutor response system...",
    "Polishing the interactive study suite dashboard..."
  ];

  const [phraseIdx, setPhraseIdx] = useState(0);

  useEffect(() => {
    const phraseTimer = setInterval(() => {
      setPhraseIdx((prev) => (prev + 1) % microPhrases.length);
    }, 1500);
    return () => clearInterval(phraseTimer);
  }, []);

  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      if (current < steps.length - 1) {
        current += 1;
        setActiveStep(current);
      } else {
        clearInterval(interval);
        onComplete?.();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="mt-6 p-6 sm:p-8 bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/30 dark:from-zinc-950/40 dark:via-zinc-900/40 dark:to-zinc-950/20 border border-indigo-150/55 dark:border-zinc-800/50 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 animate-spin" />
          <span className="text-[10px] font-mono tracking-widest text-indigo-700 dark:text-indigo-400 font-extrabold uppercase">Compilation Engine Active</span>
        </div>
        <span className="text-[10px] font-bold font-mono text-[#bf5af2] animate-pulse">GENERATING COGNITIVE WORKSPACE</span>
      </div>

      <div className="space-y-4">
        {steps.map((step, idx) => {
          const isDone = idx < activeStep;
          const isCurrent = idx === activeStep;
          return (
            <div key={idx} className="flex items-start gap-4 transition-all duration-300">
              <div className="flex flex-col items-center shrink-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold font-mono transition-all duration-300 ${
                  isDone 
                    ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/10' 
                    : isCurrent 
                      ? 'bg-indigo-600 text-white animate-pulse shadow-md shadow-indigo-600/10' 
                      : 'bg-neutral-100 dark:bg-zinc-800 text-neutral-400 border border-neutral-200 dark:border-zinc-700'
                }`}>
                  {isDone ? '✓' : idx + 1}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-0.5 h-6 my-1 transition-all duration-300 ${isDone ? 'bg-emerald-500' : 'bg-neutral-200 dark:bg-zinc-800'}`} />
                )}
              </div>
              <div className="pt-0.5 min-w-0">
                <p className={`text-xs font-semibold leading-none transition-all duration-300 ${
                  isDone 
                    ? 'text-neutral-400 line-through dark:text-zinc-500' 
                    : isCurrent 
                      ? 'text-neutral-900 dark:text-zinc-50 font-bold' 
                      : 'text-neutral-400 dark:text-zinc-500'
                }`}>
                  {step.label}
                </p>
                {isCurrent && (
                  <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-mono mt-1 animate-pulse">
                    ⚡ {microPhrases[phraseIdx]}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between text-[10px] font-mono text-neutral-400 dark:text-zinc-500">
          <span>Overall Workspace Alignment Progress</span>
          <span className="font-bold text-indigo-600 dark:text-indigo-400">{steps[activeStep].weight}%</span>
        </div>
        <div className="w-full bg-neutral-150 dark:bg-zinc-800 rounded-full h-1.5 overflow-hidden">
          <div 
            className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500" 
            style={{ width: `${steps[activeStep].weight}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const TransformationPreview = ({ inputType, learnMode }: { inputType?: string; learnMode?: boolean }) => {
  const steps = [
    { label: 'YouTube Video', icon: Video, color: 'text-rose-500 bg-rose-500/5 border border-rose-500/10' },
    { label: 'AI Analysis', icon: Cpu, color: 'text-blue-500 bg-blue-500/5 border border-blue-500/10 animate-pulse-slow' },
    { label: 'Summary', icon: FileText, color: 'text-teal-500 bg-teal-500/5 border border-teal-500/10' },
    { label: 'Flashcards', icon: Layers, color: 'text-amber-500 bg-amber-500/5 border border-amber-500/10' },
    { label: 'Mind Map', icon: Network, color: 'text-indigo-500 bg-indigo-500/5 border border-indigo-500/10' },
    { label: 'AI Chat', icon: MessageSquare, color: 'text-pink-500 bg-pink-500/5 border border-pink-500/10' },
    { label: 'Quiz', icon: HelpCircle, color: 'text-emerald-500 bg-emerald-500/5 border border-emerald-500/10' }
  ];

  return (
    <div className="p-5 bg-neutral-50 dark:bg-zinc-950/40 rounded-2xl border border-black/[0.02] dark:border-zinc-800/60 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#86868b]">Transformation Flow Preview</span>
        <span className="bg-[#0071e3]/10 text-[#0071e3] text-[8px] font-extrabold px-1.5 py-0.5 rounded uppercase font-mono tracking-wide">AI Pathway</span>
      </div>
      <div className="hidden sm:flex items-center justify-between gap-1">
        {steps.map((step, idx) => {
          const IconComp = step.icon;
          return (
            <React.Fragment key={idx}>
              <div className="flex flex-col items-center text-center space-y-1.5 flex-1 group">
                <div className={`p-2.5 rounded-xl ${step.color} transition-transform duration-300 group-hover:scale-110 shadow-sm`}>
                  <IconComp className="w-4.5 h-4.5 shrink-0" />
                </div>
                <span className="text-[10px] font-bold text-neutral-700 dark:text-zinc-300 leading-none">{step.label}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className="text-neutral-300 dark:text-zinc-700 font-mono text-xs select-none animate-pulse-soft shrink-0">
                  <ArrowRight className="w-3.5 h-3.5 text-neutral-400" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Mobile Grid */}
      <div className="sm:hidden grid grid-cols-4 gap-2">
        {steps.map((step, idx) => {
          const IconComp = step.icon;
          return (
            <div key={idx} className="flex flex-col items-center text-center p-2 rounded-xl bg-white dark:bg-zinc-900 border border-black/[0.02] dark:border-zinc-800/60">
              <div className={`p-2 rounded-lg ${step.color}`}>
                <IconComp className="w-4 h-4 shrink-0" />
              </div>
              <span className="text-[9px] font-bold text-neutral-600 dark:text-zinc-400 mt-1 truncate max-w-full leading-none">{step.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WorkspaceOutcomePreview = () => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-neutral-900 to-slate-950 text-white rounded-3xl p-6 border border-white/[0.08] shadow-2xl">
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full filter blur-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-500/10 rounded-full filter blur-xl pointer-events-none" />

      <div className="relative z-10 space-y-4">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <span className="text-[10px] font-mono tracking-widest text-indigo-400 font-extrabold uppercase">Interactive Outcome Preview</span>
          <span className="bg-emerald-500/20 text-emerald-400 text-[8px] font-extrabold px-2 py-0.5 rounded-full tracking-wider font-mono">100% AUTOMATED</span>
        </div>

        <div className="space-y-3.5 text-left font-sans">
          <div className="flex items-center gap-3 bg-white/5 p-2.5 rounded-xl border border-white/5">
            <span className="text-xl">📺</span>
            <div>
              <p className="text-xs font-bold text-white leading-none">Your Video / Document Link</p>
              <p className="text-[9px] text-slate-400 font-mono mt-0.5">Source: YouTube, Local Files, websites</p>
            </div>
          </div>

          <div className="flex justify-center my-1 text-slate-500 font-mono text-base select-none animate-bounce">
            ↓
          </div>

          <div className="space-y-2.5">
            {[
              { icon: '📝', title: '12 Key Notes & Milestones', desc: 'Structured speed summaries with embedded video references' },
              { icon: '🧠', title: '24 Spaced-Repetition Cards', desc: 'AI flashcards to test and verify cognitive recall instantly' },
              { icon: '🗺', title: 'Dynamic Interactive Mind Map', desc: 'Editable visual conceptual maps built on your nodes' },
              { icon: '❓', title: '15 Adaptive Diagnostics', desc: 'Verification quizzes with rich detailed option rationales' },
              { icon: '💬', title: 'AI Chat Study Assistant', desc: 'Conversational companion to answer workspace questions' }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-3 items-start group">
                <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/5 flex items-center justify-center text-sm shrink-0 transition-transform duration-300 group-hover:scale-110">
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs font-bold text-white group-hover:text-indigo-300 transition">{item.title}</p>
                  <p className="text-[10px] text-slate-400 font-light leading-snug">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-3 flex items-center justify-between text-[11px] text-slate-400 font-sans">
          <div className="flex items-center gap-1">
            <span className="text-emerald-400 animate-pulse">●</span>
            <span>Est. Generation Speed</span>
          </div>
          <span className="font-mono font-bold text-white">⏱ under 60 seconds</span>
        </div>
      </div>
    </div>
  );
};

const FeatureHighlightsGrid = () => {
  const features = [
    { icon: '📄', title: 'AI Notes', desc: 'Generate structured summaries and deep learning nodes instantly.' },
    { icon: '🧠', title: 'Flashcards', desc: 'Active recall drills designed to help you remember faster.' },
    { icon: '🗺', title: 'Mind Maps', desc: 'Visual nodes and dynamic cognitive graphs for understanding.' },
    { icon: '💬', title: 'AI Chat', desc: 'Ask anything about your content, test your theories instantly.' },
    { icon: '❓', title: 'Quiz Generator', desc: 'Assess your skills with diagnostic questions and rationales.' },
    { icon: '📤', title: 'Export Anywhere', desc: 'Save your customized workspace as PDF, Markdown, or Word.' }
  ];

  return (
    <div className="space-y-5 pt-8 text-left font-sans">
      <div className="space-y-1">
        <h2 className="text-xl md:text-2xl font-bold font-display text-[#1d1d1f] dark:text-zinc-50">High-Fidelity Learning Utilities</h2>
        <p className="text-xs text-neutral-500 dark:text-zinc-400 font-light">Every workspace compiles the complete suite of analytical assets automatically</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {features.map((feat, idx) => (
          <div key={idx} className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-black/[0.04] dark:border-zinc-800 hover:border-[#0071e3]/30 dark:hover:border-indigo-500/30 hover:scale-105 transition-all duration-200 cursor-pointer group flex items-start gap-3 min-h-[140px]">
            <span className="text-2xl shrink-0 select-none">{feat.icon}</span>
            <div className="space-y-1 min-w-0">
              <h3 className="text-sm font-bold text-neutral-800 dark:text-zinc-200 group-hover:text-[#0071e3] transition">{feat.title}</h3>
              <p className="text-[11px] text-neutral-500 dark:text-zinc-400 font-light leading-snug">{feat.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

import MobileBottomNav from './components/MobileBottomNav';
import AdminDashboard from './components/AdminDashboard';
import { initGA, trackGAEvent, trackPageView, getSessionEvents, TrackedEvent, clearSessionEvents } from './utils/analytics';

const getEmbedUrl = (url: string) => {
  if (!url) return null;
  const cleaned = url.trim();
  
  // YouTube matches
  let ytMatch = cleaned.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i);
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1] === 'UF8uR6Z6KLc' ? 'Hd_pt-xlV50' : ytMatch[1];
    return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0` };
  }
  
  // Loom matches
  let loomMatch = cleaned.match(/loom\.com\/(?:share|embed)\/([a-f0-9]+)/i);
  if (loomMatch && loomMatch[1]) {
    return { type: 'loom', embedUrl: `https://www.loom.com/embed/${loomMatch[1]}` };
  }
  
  // Vimeo matches
  let vimeoMatch = cleaned.match(/vimeo\.com\/(?:video\/)?([0-9]+)/i);
  if (vimeoMatch && vimeoMatch[1]) {
    return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
  }
  
  // Check if direct video file
  if (cleaned.match(/\.(mp4|webm|ogg|mov)(?:\?|$)/i)) {
    return { type: 'direct', embedUrl: cleaned };
  }
  
  // Default fallback if already an embed URL or other
  return { type: 'other', embedUrl: cleaned };
};

const getOrGenerateReelScript = (summary: YouTubeSummaryResponse | null): any => {
  if (!summary) return null;
  if (summary.reelScript) return summary.reelScript;
  
  // Deterministic fallback script compiled gracefully from existing insights
  const title = `Viral Summary: ${summary.metadata.title.slice(0, 45)}...`;
  const takeaways = (summary.takeaways || []).map((t: any) => typeof t === 'string' ? t : (t?.text || ''));
  const baseTitle = summary.metadata.title;
  const author = summary.metadata.author;

  const scenes = [
    {
      sceneNumber: 1,
      durationSeconds: 5,
      visualHook: "Extreme close-up zoom. Bold caption overlay flashing in center screen with crisp synth-bass sound.",
      voiceover: `Have you ever heard of "${baseTitle}"? Here is the ultimate short-form breakdown of ${author}'s viral advice.`,
      textOverlay: "The Truth Revealed"
    },
    {
      sceneNumber: 2,
      durationSeconds: 8,
      visualHook: "Fast vertical pan slide. Cinematic active B-roll with high contrast lines.",
      voiceover: `Key trap exposed: ${takeaways[0] || 'Stop pursuing shallow metrics and start focused consistency.'}`,
      textOverlay: "Core Myth Shattered"
    },
    {
      sceneNumber: 3,
      durationSeconds: 8,
      visualHook: "Minimalist screen split dynamic slide showing data progression.",
      voiceover: `Crucial breakthrough: ${takeaways[1] || 'Real performance starts when you cut out are irrelevant meetings.'}`,
      textOverlay: "Secret To Progress"
    },
    {
      sceneNumber: 4,
      durationSeconds: 8,
      visualHook: "Upward camera pan. Modern split panel with highlighted key metrics.",
      voiceover: `Next big asset: ${takeaways[2] || 'Execution is worth infinitely more than pure strategy without motion.'}`,
      textOverlay: "Execution > Ideas"
    },
    {
      sceneNumber: 5,
      durationSeconds: 6,
      visualHook: "Close focus with visual pulse effects on screen.",
      voiceover: `In summary: ${summary.summary.split('.')[0] || 'This changes how you approach learning.'}.`,
      textOverlay: "The Big Lesson"
    },
    {
      sceneNumber: 6,
      durationSeconds: 5,
      visualHook: "Branded Call To Action prompt flashing high contrast on charcoal backdrop.",
      voiceover: "Swipe up or tap link to view the entire interactive concept mindmap tool right now!",
      textOverlay: "UNCOVER SECRETS"
    }
  ];

  const totalDuration = scenes.reduce((sum, s) => sum + s.durationSeconds, 0);

  return {
    title,
    hookType: "Inquiry Loop / Myth-Buster",
    estimatedDuration: totalDuration,
    themeSuggestion: "Charcoal dark elegant stage, vibrant yellow and bold white centered fonts, ultra-rapid cuts, epic background drone pulse",
    scenes,
    readyMadeCaption: `🤯 Ultimate 60-second summary of "${baseTitle}" by ${author}! Tap the link to view complete interactive timeline & quiz tools. 

#reels #creative #marketing #${author.replace(/\s+/g, '')}`,
    callToAction: "Click the bio to explore the full interactive mastermind summary!"
  };
};

const downloadSRT = (script: any) => {
  if (!script) return;
  let srtContent = "";
  let cumulativeSeconds = 0;
  script.scenes.forEach((scene: any, index: number) => {
    const startMs = cumulativeSeconds * 1000;
    const endMs = (cumulativeSeconds + scene.durationSeconds) * 1000;
    cumulativeSeconds += scene.durationSeconds;

    const formatTime = (ms: number) => {
      const h = Math.floor(ms / 3600000).toString().padStart(2, '0');
      const m = Math.floor((ms % 3600000) / 60000).toString().padStart(2, '0');
      const s = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
      const msStr = (ms % 1000).toString().padStart(3, '0');
      return `${h}:${m}:${s},${msStr}`;
    };

    srtContent += `${index + 1}\n`;
    srtContent += `${formatTime(startMs)} --> ${formatTime(endMs)}\n`;
    srtContent += `[${scene.textOverlay.toUpperCase()}]\n`;
    srtContent += `${scene.voiceover}\n\n`;
  });

  const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${script.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_captions.srt`;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadVTT = (script: any) => {
  if (!script) return;
  let vttContent = "WEBVTT\n\n";
  let cumulativeSeconds = 0;
  script.scenes.forEach((scene: any, index: number) => {
    const startMs = cumulativeSeconds * 1000;
    const endMs = (cumulativeSeconds + scene.durationSeconds) * 1000;
    cumulativeSeconds += scene.durationSeconds;

    const formatTime = (ms: number) => {
      const h = Math.floor(ms / 3600000).toString().padStart(2, '0');
      const m = Math.floor((ms % 3600000) / 60000).toString().padStart(2, '0');
      const s = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
      const msStr = (ms % 1000).toString().padStart(3, '0');
      return `${h}:${m}:${s}.${msStr}`;
    };

    vttContent += `${index + 1}\n`;
    vttContent += `${formatTime(startMs)} --> ${formatTime(endMs)}\n`;
    vttContent += `[${scene.textOverlay.toUpperCase()}]\n`;
    vttContent += `${scene.voiceover}\n\n`;
  });

  const blob = new Blob([vttContent], { type: 'text/vtt;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${script.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_captions.vtt`;
  link.click();
  URL.revokeObjectURL(url);
};

const downloadMarkdownScript = (script: any) => {
  if (!script) return;
  let md = `# SHORTENED VIDEO STORYBOARD: ${script.title}\n\n`;
  md += `**Hook Style:** ${script.hookType}\n`;
  md += `**Estimated Duration:** ${script.estimatedDuration} seconds\n`;
  md += `**Styling / Theme Direction:** ${script.themeSuggestion}\n\n`;
  md += `## STORYBOARD TIMELINE SCENES\n\n`;
  
  script.scenes.forEach((scene: any) => {
    md += `### SCENE ${scene.sceneNumber} (${scene.durationSeconds}s)\n`;
    md += `- **Visual Directions / B-Roll:** ${scene.visualHook}\n`;
    md += `- **Voiceover Narration:** "${scene.voiceover}"\n`;
    md += `- **On-Screen Subtitle/Text Overlay:** [${scene.textOverlay}]\n\n`;
  });

  md += `## ENGAGEMENT CAPTION & CALL TO ACTION\n\n`;
  md += `**Platform Ready-Made Caption:**\n\`\`\`text\n${script.readyMadeCaption}\n\`\`\`\n\n`;
  md += `**Call To Action (CTA):** ${script.callToAction}\n`;

  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${script.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_script.md`;
  link.click();
  URL.revokeObjectURL(url);
};

export default function App() {
  // Initialize global client error logging on mount
  useEffect(() => {
    initGlobalErrorLogging(() => {
      const u = auth.currentUser;
      return { uid: u?.uid, email: u?.email || undefined };
    });
  }, []);

  // Firebase Auth Visitor state
  const [visitorUser, setVisitorUser] = useState<User | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [googleAuthError, setGoogleAuthError] = useState<{ message: string; code?: string } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setVisitorUser(user);
      setAuthInitialized(true);
      if (user) {
        if (user.email && user.email.toLowerCase().trim() === 'rbahirathan@gmail.com') {
          try {
            localStorage.setItem('youtube_summarizer_premium', 'true');
            localStorage.setItem('youtube_summarizer_plan', 'enterprise');
            localStorage.setItem('youtube_summarizer_premium_email', user.email.trim());
            setIsPremium(true);
            setCurrentPlan('enterprise');
          } catch (e) {
            console.warn('Could not persist premium status for rbahirathan@gmail.com:', e);
          }
        } else if (user.email) {
          fetch(`/api/subscription-status?email=${encodeURIComponent(user.email)}`)
            .then(res => res.json())
            .then(data => {
              if (data?.subscription?.status === 'active' && data.subscription.plan) {
                const p = data.subscription.plan.toLowerCase();
                if (p === 'enterprise' || p === 'pro') {
                  setCurrentPlan(p as PlanType);
                  setIsPremium(true);
                  try {
                    localStorage.setItem('youtube_summarizer_plan', p);
                    localStorage.setItem('youtube_summarizer_premium', 'true');
                    localStorage.setItem('youtube_summarizer_premium_email', user.email!);
                  } catch {}
                }
              }
            })
            .catch(() => {});
        }
        try {
          // 1. Log user via backend API first
          fetch('/api/log-google-user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              uid: user.uid,
              email: user.email || '',
              displayName: user.displayName || '',
              photoURL: user.photoURL || ''
            })
          }).catch(err => console.warn('Backend logging API status:', err));

          // 2. Also log directly to Firestore on the client-side!
          // This uses the user's logged-in identity/auth token and guarantees writing is successful in all environments.
          const userRef = doc(db, 'google_users', user.uid);
          const now = new Date().toISOString();
          const userSnap = await getDoc(userRef).catch(() => null);
          if (!userSnap || !userSnap.exists()) {
            await setDoc(userRef, {
              uid: user.uid,
              email: user.email || '',
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
              createdAt: now,
              lastLoginAt: now,
            });
          } else {
            await setDoc(userRef, {
              email: user.email || '',
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
              lastLoginAt: now,
            }, { merge: true });
          }
          console.log('Successfully saved user details to Firestore directly on client-side.');
        } catch (err) {
          console.error('Error logging Google user details to firestore:', err);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Handle incoming Passwordless Email Sign-In Link
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please confirm your sign-in email address:');
      }
      if (email) {
        setLoading(true);
        setLoadingStep('Verifying secure magic sign-in link...');
        signInWithEmailLink(auth, email, window.location.href)
          .then((result) => {
            window.localStorage.removeItem('emailForSignIn');
            // Clean url
            window.history.replaceState({}, document.title, window.location.pathname);
            setLoading(false);
          })
          .catch((err) => {
            console.error('Magic link authentication failed:', err);
            setLoading(false);
            alert('Failed to authenticate magic link. Please try again or request a new link.');
          });
      }
    }
  }, []);

  // Status & states
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [isPremium, setIsPremium] = useState<boolean>(() => {
    try {
      const plan = localStorage.getItem('youtube_summarizer_plan')?.toLowerCase().trim();
      const prem = localStorage.getItem('youtube_summarizer_premium');
      return prem === 'true' || plan === 'pro' || plan === 'enterprise';
    } catch {
      return false;
    }
  });

  const [currentPlan, setCurrentPlan] = useState<PlanType>(() => {
    try {
      const email = localStorage.getItem('youtube_summarizer_premium_email');
      if (email && email.toLowerCase().trim() === 'rbahirathan@gmail.com') return 'enterprise';
      const plan = localStorage.getItem('youtube_summarizer_plan')?.toLowerCase().trim();
      if (plan === 'enterprise' || plan === 'pro' || plan === 'free') return plan as PlanType;
      const prem = localStorage.getItem('youtube_summarizer_premium');
      if (prem === 'true') return 'pro';
      return 'free';
    } catch {
      return 'free';
    }
  });

  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [planModalInitialPlan, setPlanModalInitialPlan] = useState<PlanType>('pro');
  const [planModalInitialCycle, setPlanModalInitialCycle] = useState<BillingCycle>('monthly');

  const openPlanModal = (targetPlan?: PlanType, cycle?: BillingCycle) => {
    setPlanModalInitialPlan(targetPlan || (currentPlan === 'free' ? 'pro' : currentPlan));
    setPlanModalInitialCycle(cycle || 'monthly');
    setIsPlanModalOpen(true);
  };

  const handlePlanChanged = (newPlan: PlanType) => {
    setCurrentPlan(newPlan);
    const hasPremium = newPlan === 'pro' || newPlan === 'enterprise';
    setIsPremium(hasPremium);
    try {
      localStorage.setItem('youtube_summarizer_plan', newPlan);
      if (hasPremium) {
        localStorage.setItem('youtube_summarizer_premium', 'true');
      } else {
        localStorage.removeItem('youtube_summarizer_premium');
      }
      if (visitorUser?.email) {
        localStorage.setItem('youtube_summarizer_premium_email', visitorUser.email);
      }
    } catch (e) {
      console.warn('localStorage error updating plan:', e);
    }
  };

  // Check URL query parameters for Stripe checkout success
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('checkout_success') === 'true') {
        handlePlanChanged('pro');
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        openPlanModal('pro');
      }
    }
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [activeSummary, setActiveSummary] = useState<YouTubeSummaryResponse | null>(null);
  const [currentScreen, setCurrentScreen] = useState<'landing' | 'workspace' | 'journey'>('landing');
  const [currentLoadingTipIdx, setCurrentLoadingTipIdx] = useState(0);

  // Path-based routing state
  const [currentPath, setCurrentPath] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname || '/';
    }
    return '/';
  });

  const [isDark, setIsDark] = useState<boolean>(() => {
    try {
      return localStorage.getItem('zipytiny_theme') === 'dark' || (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    } catch {
      return false;
    }
  });

  // Initialize Google Analytics 4 on initial mount
  useEffect(() => {
    const gaId = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID || 'G-L5JRKLMZLV';
    initGA(gaId);
  }, []);

  // Keep currentPath in sync with browser navigation (Back / Forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (rawPath: string) => {
    if (typeof window !== 'undefined') {
      let path = rawPath;
      const [pathname, search] = rawPath.split('?');
      if (
        pathname !== '/' &&
        !pathname.endsWith('/') &&
        !pathname.includes('.')
      ) {
        path = `${pathname}/${search ? `?${search}` : ''}`;
      }
      window.history.pushState({}, '', path);
      setCurrentPath(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Normalized clean path without trailing slashes
  const cleanPath = currentPath.split('?')[0].replace(/\/+$/, '') || '/';

  // Synchronize SEO tags and document title for the active route
  const currentSeoMeta = useMemo(() => getRouteSeoMeta(cleanPath), [cleanPath]);
  useSeoMeta(currentSeoMeta);

  // Track SPA Page Views across route transitions in Google Analytics 4
  useEffect(() => {
    trackPageView(cleanPath, currentSeoMeta.title);
  }, [cleanPath, currentSeoMeta.title]);

  const handleGoogleSignIn = async () => {
    try {
      setGoogleAuthError(null);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const userCred = await signInWithPopup(auth, provider);
      trackGAEvent('login', { method: 'google', email: userCred.user.email });
    } catch (err: any) {
      console.error('Google Sign In Error:', err);
      setGoogleAuthError({ message: err.message, code: err.code });
      trackGAEvent('login_failed', { method: 'google', error_code: err.code });
    }
  };

  const handleStartFreeSummary = async (
    input: string,
    type: 'video' | 'website' | 'file' | 'text' = 'video',
    filesList?: any[],
    depth: 'quick' | 'study' | 'mastery' = 'study'
  ) => {
    if (!input && (!filesList || filesList.length === 0)) return;

    const trimmed = (input || '').trim();
    setError(null);
    setLoading(true);
    setLoadingStep('Connecting to content source & analyzing material...');

    trackGAEvent('generate_summary_started', {
      type,
      depth,
      has_files: Boolean(filesList && filesList.length > 0),
    });

    // 1. Fast check for preloaded curriculum videos
    const matchedPreloaded = PRELOADED_VIDEOS.find(
      (p) =>
        (p.metadata?.videoId && trimmed.includes(p.metadata.videoId)) ||
        (p.metadata?.videoUrl && trimmed === p.metadata.videoUrl)
    );

    if (matchedPreloaded) {
      setActiveSummary(matchedPreloaded);
      setLoading(false);
      trackGAEvent('generate_summary_completed', {
        type,
        depth,
        is_preloaded: true,
        title: matchedPreloaded.metadata?.title,
      });
      navigateTo('/workspace/');
      return;
    }

    try {
      setLoadingStep('Transcribing audio & compiling high-yield study notes...');

      const response = await fetch('/api/summarize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoUrl: trimmed,
          learningDepth: depth,
          learnMode: true,
          files: filesList,
        }),
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || `Processing failed with status ${response.status}`);
      }

      const summaryData: YouTubeSummaryResponse = await response.json();
      setActiveSummary(summaryData);
      setLoading(false);
      trackGAEvent('generate_summary_completed', {
        type,
        depth,
        is_preloaded: false,
        title: summaryData.metadata?.title,
      });
      navigateTo('/workspace/');
    } catch (err: any) {
      console.error('Failed to summarize URL:', err);
      setError(err.message || 'Unable to generate study workspace. Please try again.');
      setLoading(false);
      trackGAEvent('generate_summary_failed', {
        type,
        depth,
        error: err.message,
      });
    }
  };

  const renderActiveScreen = () => {
    // 0. Administrative Console (/admin)
    if (cleanPath === '/admin') {
      return (
        <AdminDashboard
          onNavigateHome={() => navigateTo('/')}
        />
      );
    }

    // 1. Client Meeting Recap Use Case
    if (cleanPath === '/use-cases/client-meeting-recap') {
      return (
        <ClientMeetingRecapUseCasePage
          onNavigateHome={() => navigateTo('/')}
          onLaunchApp={() => {
            navigateTo('/workspace/');
          }}
        />
      );
    }

    // 2. Sales Calls Use Case
    if (cleanPath === '/use-cases/sales-calls') {
      return (
        <SalesCallsUseCasePage
          onNavigateHome={() => navigateTo('/')}
          onLaunchApp={() => {
            navigateTo('/workspace/');
          }}
        />
      );
    }

    // 2b. Recording to PowerPoint Use Case
    if (cleanPath === '/use-cases/recording-to-powerpoint') {
      return (
        <RecordingToPowerPointUseCasePage
          onNavigateHome={() => navigateTo('/')}
          onLaunchApp={() => {
            navigateTo('/workspace/?focus=input&section=presentation');
          }}
        />
      );
    }

    // 2b-2. Zipytiny vs Otter.ai Comparison (/vs/otter-ai)
    if (cleanPath === '/vs/otter-ai') {
      return (
        <ZipytinyVsOtterAiPage
          onNavigateHome={() => navigateTo('/')}
          onLaunchApp={() => {
            navigateTo('/workspace/?focus=input');
          }}
          onNavigateToPricing={() => navigateTo('/pricing/')}
          onNavigateToPath={(path: string) => navigateTo(path)}
        />
      );
    }

    // 2b-3. YouTube Video → Interactive Quiz (/youtube-quiz and /quiz/:id)
    if (cleanPath === '/youtube-quiz' || cleanPath === '/quiz' || cleanPath.startsWith('/quiz/')) {
      const quizIdParam = cleanPath.startsWith('/quiz/') ? cleanPath.replace('/quiz/', '') : undefined;
      return (
        <YouTubeQuizPage
          quizId={quizIdParam}
          isDark={isDark}
          onNavigateHome={() => navigateTo('/')}
          onNavigateToQuiz={(id: string) => navigateTo(`/quiz/${id}/`)}
          onLaunchWorkspace={(videoUrl?: string) => {
            if (videoUrl) {
              handleStartFreeSummary(videoUrl, 'video');
            } else {
              navigateTo('/workspace/?focus=input&tab=quiz');
            }
          }}
        />
      );
    }

    // 2b-4. Programmatic Video Study Library & Anki Decks (/study and /study/:slug)
    if (cleanPath === '/study' || cleanPath.startsWith('/study/')) {
      const studySlugParam = cleanPath === '/study' ? undefined : cleanPath.replace('/study/', '');
      return (
        <StudyLibraryPage
          slug={studySlugParam}
          isDark={isDark}
          onNavigateHome={() => navigateTo('/')}
          onNavigateToStudy={(slug: string) => navigateTo(slug ? `/study/${slug}/` : '/study/')}
          onLaunchWorkspace={(videoUrl?: string) => {
            if (videoUrl) {
              handleStartFreeSummary(videoUrl, 'video');
            } else {
              navigateTo('/workspace/?focus=input&tab=quiz&subtab=flashcards');
            }
          }}
        />
      );
    }

    // 2c. Standalone Pricing Page (/pricing)
    if (cleanPath === '/pricing') {
      return (
        <PricingPage
          currentPlan={currentPlan}
          visitorUser={visitorUser}
          onNavigateHome={() => navigateTo('/')}
          onSelectPlan={(planCode: PlanType, cycle?: 'monthly' | 'yearly') => {
            openPlanModal(planCode, cycle);
          }}
          onLaunchFree={() => {
            if (currentPlan === 'free') {
              navigateTo('/workspace/?focus=input');
            } else {
              openPlanModal('free');
            }
          }}
          onNavigateToFeatures={() => navigateTo('/features/')}
        />
      );
    }

    // 2d. Features Hub Page (/features)
    if (cleanPath === '/features') {
      return (
        <FeaturesHubPage
          onNavigateHome={() => navigateTo('/')}
          onNavigateToFeature={(slug: string) => navigateTo(`/features/${slug}/`)}
          onLaunchApp={() => navigateTo('/workspace/?focus=input')}
          onNavigateToPricing={() => navigateTo('/pricing/')}
        />
      );
    }

    // 3. Feature Pages (/features/:slug)
    if (cleanPath.startsWith('/features/')) {
      const featureSlug = cleanPath.replace('/features/', '');
      return (
        <FeaturePage
          featureSlug={featureSlug}
          isDark={isDark}
          onNavigateHome={() => navigateTo('/')}
          onLaunchApp={(targetTab?: string, targetSubTab?: string) => {
            const queryParts = ['focus=input'];
            if (featureSlug) queryParts.push(`feature=${encodeURIComponent(featureSlug)}`);
            if (targetTab) queryParts.push(`tab=${encodeURIComponent(targetTab)}`);
            if (targetSubTab) queryParts.push(`subtab=${encodeURIComponent(targetSubTab)}`);
            navigateTo(`/workspace/?${queryParts.join('&')}`);
          }}
          onNavigateToPath={(path: string) => {
            navigateTo(path);
          }}
        />
      );
    }

    // 4. Learning Blog Pages (/blog and /blog/:slug)
    if (cleanPath === '/blog' || cleanPath.startsWith('/blog/')) {
      const blogSlug = cleanPath === '/blog' ? '' : cleanPath.replace('/blog/', '');
      return (
        <BlogPage
          currentBlogSlug={blogSlug}
          onNavigateHome={() => navigateTo('/')}
          onNavigateToBlog={(slug: string) => navigateTo(slug ? `/blog/${slug}/` : '/blog/')}
          onLaunchApp={() => navigateTo('/workspace/?focus=input')}
          onStartFreeSummary={handleStartFreeSummary}
        />
      );
    }

    // 5. Interactive SEO Tools (/tools and /tools/:slug)
    if (cleanPath === '/tools' || cleanPath.startsWith('/tools/')) {
      const toolSlug = cleanPath === '/tools' ? 'youtube-lecture-summarizer' : cleanPath.replace('/tools/', '');
      return (
        <ToolPage
          currentToolSlug={toolSlug}
          onNavigateHome={() => navigateTo('/')}
          onLaunchApp={() => {
            navigateTo('/workspace/?focus=input');
          }}
        />
      );
    }

    // 6. FAQ Page
    if (cleanPath === '/faq') {
      return (
        <FaqPage
          onNavigateToPath={(path: string) => navigateTo(path)}
          onLaunchWorkspace={() => navigateTo('/workspace/?focus=input')}
          onNavigateScreen={(screen: string) => {
            if (screen === 'landing') navigateTo('/');
            else if (screen === 'blog') navigateTo('/blog/');
            else if (screen === 'extension') navigateTo('/chrome-extension/');
            else navigateTo(`/${screen}/`);
          }}
        />
      );
    }

    // 7. Chrome Extension Page
    if (cleanPath === '/chrome-extension' || cleanPath === '/extension') {
      return (
        <ChromeExtensionPage
          isDark={isDark}
          onLaunchApp={() => navigateTo('/workspace/?focus=input')}
        />
      );
    }

    // 8. Interactive Workspace (/workspace, /app, /s/:id, /share/:id)
    if (cleanPath === '/workspace' || cleanPath === '/app' || cleanPath.startsWith('/s/') || cleanPath.startsWith('/share/')) {
      return (
        <LearningWorkspace
          activeSummary={activeSummary || PRELOADED_VIDEOS[0]}
          onBackToCenter={() => navigateTo('/')}
          ytStartSeconds={null}
          onJumpToTimestamp={() => {}}
          onResetJump={() => {}}
          isPremium={isPremium}
          currentPlan={currentPlan}
          onOpenPlanModal={(plan) => openPlanModal(plan)}
          onNavigateToPricing={() => navigateTo('/pricing/')}
          visitorUser={visitorUser}
          setShowAuthModal={() => {}}
          setAuthModalPurpose={() => {}}
          setShowStripeModal={() => openPlanModal('pro')}
          setSelectedPlanCode={(code: string) => openPlanModal(code as any)}
          setStripePaymentSuccess={() => {}}
          downloadSummaryAsPDF={() => {}}
          handleGenerateTTS={() => {}}
          ttsLoading={false}
          audioUrl={null}
          isPlaying={false}
          togglePlay={() => {}}
          audioProgress={0}
          audioDuration={0}
          formatTime={(s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`}
          isRtl={false}
          t={(k: string) => k}
          getHeaders={() => ({})}
          onStartFreeSummary={handleStartFreeSummary}
        />
      );
    }

    // Default: Landing Page (Handles / and /pricing)
    return (
      <LandingPage
        onLaunchApp={(targetTab?: string, targetSubTab?: string) => {
          const queryParts = ['focus=input'];
          if (targetTab) queryParts.push(`tab=${encodeURIComponent(targetTab)}`);
          if (targetSubTab) queryParts.push(`subtab=${encodeURIComponent(targetSubTab)}`);
          navigateTo(`/workspace/?${queryParts.join('&')}`);
        }}
        onNavigateToFeature={(featureSlug: string) => navigateTo(`/features/${featureSlug}/`)}
        onNavigateToBlog={(blogSlug: string) => navigateTo(blogSlug ? `/blog/${blogSlug}/` : '/blog/')}
        onNavigateToPath={(path: string) => navigateTo(path)}
        onStartFreeSummary={handleStartFreeSummary}
        onUpgrade={() => openPlanModal('pro')}
        onSelectPlan={(planCode: PlanType, cycle?: 'monthly' | 'yearly') => openPlanModal(planCode, cycle)}
        currentPlan={currentPlan}
        isPremium={isPremium}
        visitorUser={visitorUser}
        onGoogleSignIn={handleGoogleSignIn}
      />
    );
  };

  return (
    <div className="bg-white dark:bg-zinc-950 min-h-screen relative">
      <ErrorBoundary fallback={<AppScreenLoadingFallback />}>
        {renderActiveScreen()}
      </ErrorBoundary>

      {/* Generating Study Workspace Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="max-w-xl w-full bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl p-6 border border-neutral-200 dark:border-zinc-800 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
                <h3 className="font-bold text-base text-neutral-900 dark:text-white">
                  Creating Your Study Workspace
                </h3>
              </div>
              <button
                onClick={() => setLoading(false)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-neutral-100 dark:bg-zinc-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-zinc-700 transition"
              >
                Cancel
              </button>
            </div>
            <LoadingTimeline loadingStep={loadingStep} />
          </div>
        </div>
      )}

      {/* Error notification */}
      {error && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md bg-rose-50 dark:bg-rose-950/90 border border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-200 p-4 rounded-2xl shadow-xl flex items-start gap-3">
          <div className="text-rose-500 font-bold text-lg mt-0.5">⚠️</div>
          <div className="flex-1 text-xs">
            <p className="font-bold text-sm">Summarization Notice</p>
            <p className="mt-1 leading-relaxed">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-rose-400 hover:text-rose-700 dark:hover:text-rose-100 text-sm font-bold ml-1 cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      <FloatingSupportChatWidget
        onNavigateToPath={(path: string) => navigateTo(path)}
        onLaunchWorkspace={() => navigateTo('/workspace/?focus=input')}
      />

      {/* Plan Selection, Upgrade & Stripe Checkout Modal */}
      <PlanCheckoutModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        initialPlan={planModalInitialPlan}
        initialBillingCycle={planModalInitialCycle}
        currentPlan={currentPlan}
        visitorUser={visitorUser}
        onPlanChanged={handlePlanChanged}
        onNavigateToWorkspace={() => navigateTo('/workspace/?focus=input')}
      />
    </div>
  );
}
